import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { IdentityUser, ApiToken, LoginOptions, RefreshLoginOptions } from './security-models';
import { EMPTY, Observable, Subject, merge, Subscription, throwError, of } from 'rxjs';
import {
  tap,
  catchError,
  shareReplay,
  map,
  startWith,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';
import * as moment from 'moment-timezone';
import * as _ from 'lodash';
import { EnvironmentService } from '../environment/environment.service';
import { Environment } from '../environment/environment';
import { Router } from '@angular/router';
import { AppUrls } from '../utility/appurls';
import { RandomService } from '../utility/random.service';
import { PaginatedResponse } from '../utility/paginated-response';
import { Group } from '../shared-models/group';
import { EntityTypeCode } from '../utility/system-type-constants';
import { ExternalApiUrls } from '../utility/apiurls-external';
import { InternalApiUrls } from '../utility/apiurls-internal';
import {
  CombineOptions,
  RetryOption,
  WillHandleErrorMessagingOption,
} from '../utility/http-options';
//import { AppInsightsService, SeverityLevel } from '@markpieszak/ng-application-insights';
//import { SeverityLevel } from '@markpieszak/ng-application-insights';
import { AppInsightsService } from '../logging/app-insights-service';
import { SnackbarService } from 'projects/bis-shared/src/lib/snackbar/snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class IdentityService {
  private http = inject(HttpClient);
  private internalApiUrls = inject(InternalApiUrls);
  private externalApiUrls = inject(ExternalApiUrls);
  private environmentService = inject(EnvironmentService);
  private router = inject(Router);
  private randomService = inject(RandomService);
  private snackbarService = inject(SnackbarService);
  private appInsightsService = inject(AppInsightsService);

  private onLogin = new Subject<void>();
  private onLogout = new Subject<void>();
  private loginChanged = new Subject<void>();
  public isLoggedIn$ = new Observable<boolean>();
  public accessToken$ = new Observable<string>();

  private _forceLogoutKey = 'bis_force_logout';
  public get forceLogout(): boolean {
    return localStorage.getItem(this._forceLogoutKey) === 'true';
  }
  public set forceLogout(newValue: boolean) {
    localStorage.setItem(this._forceLogoutKey, newValue?.toString());
  }

  private _identityKey = 'bis_int_identity';
  get identity(): IdentityUser {
    const storedIdentity = localStorage.getItem(this._identityKey);
    if (storedIdentity) {
      return JSON.parse(storedIdentity);
    } else {
      return null;
    }
  }
  set identity(newValue: IdentityUser) {
    localStorage.setItem(this._identityKey, JSON.stringify(newValue));
    // if (newValue) {
    //   this.appInsightsService.setAuthenticatedUserContext(
    //     newValue.userName,
    //     String(newValue.userId),
    //     true,
    //   );
    // } else {
    //   this.appInsightsService.clearAuthenticatedUserContext();
    // }
  }

  get displayName(): string {
    const idu = this.identity;

    if (!idu) {
      return null;
    }

    return `${idu.lastName}, ${idu.firstName}`;
  }

  get displayUserName(): string {
    const idu = this.identity;

    if (!idu) {
      return null;
    }

    return `${idu.firstName} ${idu.lastName}`;
  }

  private _redirectKey = 'bis_redirect_path';
  get redirectPath(): string {
    const storedredirect = localStorage.getItem(this._redirectKey);
    return storedredirect ? storedredirect : AppUrls.home();
  }

  set redirectPath(newValue: string) {
    localStorage.setItem(this._redirectKey, newValue);
  }

  private _apiTokenKey = 'bis_int_token';
  get apiToken(): ApiToken {
    const storedToken = localStorage.getItem(this._apiTokenKey);
    if (storedToken) {
      return JSON.parse(storedToken);
    } else {
      return null;
    }
  }

  set apiToken(newValue: ApiToken) {
    localStorage.setItem(this._apiTokenKey, JSON.stringify(newValue));
  }

  private _config: Environment;

  get isValidLogin(): boolean {
    //Prevously, response.valid alone was used to determine login validity and set this flag upon login.
    //Now we are checking for the existence of an identity...
    if (!this.identity) {
      return false;
    }

    //...that also matches the site (internal/external).
    //Mismatch on identity should not occur in shared environments because of distinct domains,
    //but likely during local testing using the same domain.
    if (this.identity?.isExternalUser !== this.environmentService.isExternalSite) {
      // const validUserError = {
      //   name: 'UnexpectedIdentity',
      //   message: 'User information did not match the site.',
      // };
      //this.appInsightsService.trackException(validUserError);
      this.appInsightsService.trackTrace('User information did not match the site.');
      return false;
    }
    return true;
  }

  private identityReloaded = new Subject<IdentityUser | void>();

  identity$: Observable<IdentityUser>;

  login$: Observable<any>;
  private loginSubscription: Subscription;

  private userGroups: Observable<Group[]>;

  private userUrls: any;

  
 

  constructor() {
    this._config = this.environmentService.getEnvironment();
    this.identity$ = merge(
      this.identityReloaded,
      this.onLogout.pipe(map(() => <IdentityUser>null)),
    ).pipe(
      startWith(this.identity),
      map(() => this.identity),
    );

    this.isLoggedIn$ = merge(this.onLogin, this.onLogout, this.loginChanged).pipe(
      startWith(this.isLoggedIn()),
      map(this.isLoggedIn),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.accessToken$ = merge(this.onLogin, this.onLogout, this.loginChanged).pipe(
      startWith(this.apiToken?.accessToken),
      map(() => this.apiToken?.accessToken),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.userUrls = this.environmentService.isExternalSite
      ? this.externalApiUrls.user
      : this.internalApiUrls.user;
    window.addEventListener('storage', this.storageListener);
  }

  private storageListener = (event: StorageEvent) => {
    if (event.storageArea === localStorage) {
      switch (event.key) {
        case this._apiTokenKey:
          this.loginChanged.next();
          break;
        case this._identityKey:
          this.loginChanged.next();
          this.identityReloaded.next();
          break;
        case this._forceLogoutKey:
          if (this.forceLogout) {
            this.userGroups = null;
            sessionStorage.removeItem('bis_ssnSearch');
            this.onLogout.next();

            if (this.environmentService.isExternalSite) {
              this.router.navigate([AppUrls.login()]);
            } else {
              this.router.navigate([AppUrls.logout()]);
            }
          }
          break;
        default:
          break;
      }
    }
  };

  hasPermissions(permissionNames: string | string[]): boolean {
    if (this.identity == null) {
      // Unable to determine if a user has permission - return undefined
      // it will act as a falsy value, but can be handled differently
      return undefined;
    }

    let result = false;
    if (Array.isArray(permissionNames)) {
      permissionNames.forEach(permissionName => {
        if (this.determineHasPermission(permissionName)) {
          result = true;
        }
      });
    } else {
      return this.determineHasPermission(permissionNames);
    }
    return result;
  }

  hasRole(roleName: string): boolean {
    if (this.identity == null) {
      // Unable to determine if a user has permission - return undefined
      // it will act as a falsy value, but can be handled differently
      return undefined;
    }
    return this.determineHasRole(roleName);
  }

  hasAnyRoles(roleNames: string[]): boolean {
    // requires a user to have at least one of the roles passed in for auth success
    if (this.identity == null) {
      // Unable to determine if a user has permission - return undefined
      // it will act as a falsy value, but can be handled differently
      return undefined;
    }

    let result = false;
    roleNames.forEach(roleName => {
      if (this.determineHasRole(roleName)) {
        result = true;
      }
    });
    return result;
  }

  isGroupAdmin(entityTypeCode: EntityTypeCode, entityId: number) {
    let result = false;
    this.identity.entityGroups.forEach(group => {
      if (group.entityTypeCode === entityTypeCode && group.entityId === entityId) {
        result = true;
      }
    });
    return result;
  }

  public refreshTokenAndIdentityIfExpiresSoon(minutes: number) {
    if (!this.apiToken?.expirationDate) {
      return of(false);
    }
    if (moment().add(minutes, 'minutes').isBefore(moment(this.apiToken.expirationDate))) {
      return of(EMPTY);
    }

    // if (!this.apiToken?.refreshToken) {
    //   //This really shouldn't happen unless Okta is misconfigured again and we don't have a refresh token provided
    //   this.appInsightsService.trackTrace(
    //     'Unable to get new access token, refresh token unavailable.',
    //     null,
    //     SeverityLevel.Error,
    //   );
    //   return of(false);
    // }
    
    this.appInsightsService.trackTrace(
      'Unable to get new access token, refresh token unavailable.');
    
    return this.loginWithRefreshToken(true);
  }

  public refreshTokenAndIdentity(forceLogin?: boolean) {
    let error: string;
    if (!this.apiToken?.refreshToken) {
      error = 'No refresh token available for refreshTokenAndIdentity.';
    } else if (
      !this.apiToken.expirationDate ||
      !moment()
        .subtract(this.environmentService.getEnvironment().refreshValidAfterTokenMins, 'minutes')
        .isBefore(this.apiToken.expirationDate)
    ) {
      error = 'Refresh token expired.';
    }

    if (error) {
      //this.appInsightsService.trackTrace(error, null, SeverityLevel.Error);
      
      this.appInsightsService.trackTrace(error);

      return throwError(error);
    }
    return this.loginWithRefreshToken(forceLogin);
  }

  authenticateUser(redirectUrl: string = null): Observable<any> {
    // Store the current path within the application to redirect after login
    if (redirectUrl) {
      this.redirectPath = redirectUrl;
    }

    if (this.environmentService.isExternalSite) {
      this.router.navigate([AppUrls.login()]);
      return EMPTY;
    } else {
      return this.redirectToThirdPartyLogin();
    }
  }

  loginWithAuthCode(authCode: string) {
    const loginOptions = {
      authorizationCode: authCode,
    };
    // this.appInsightsService.trackTrace(
    //   'Attempting to get new access token with auth code.',
    //   { authCode: authCode ? 'true' : 'false' },
    //   SeverityLevel.Information,
    // );

    this.appInsightsService.trackTrace('Attempting to get new access token with auth code.');

    return this.login(loginOptions, true);
  }

  private loginWithRefreshToken(forceLogin?: boolean) {
    const loginOptions = {
      refreshToken: this.apiToken.refreshToken,
    };
    // this.appInsightsService.trackTrace(
    //   'Attempting to get new access token with refresh token.',
    //   { forceLogin: forceLogin?.toString() },
    //   SeverityLevel.Information,
    // );
    
    this.appInsightsService.trackTrace('Attempting to get new access token with refresh token.');

    return this.login(loginOptions, forceLogin);
  }

  private login(loginOptions: LoginOptions, forceLogin?: boolean) {
    if (this.isLoggedIn() && this.isTokenValid() && !forceLogin) {
      return of(true);
    }

    if (this.loginSubscription?.closed === false) {
      return this.login$;
    }

    this.login$ = this.http
      .post(
        this.userUrls.login(),
        loginOptions,
        CombineOptions([WillHandleErrorMessagingOption, RetryOption]),
      )
      .pipe(
        switchMap((response: any) => {
          // const identityToStore: IdentityUser = response.user;
          const isChangeSession =
            this.isLoggedIn() &&
            this.isTokenValid() &&
            response.token.refreshToken !== this.apiToken.refreshToken;

          if (isChangeSession) {
            const logOutUrl = this.environmentService.isExternalSite
              ? this.externalApiUrls.user.logout()
              : this.internalApiUrls.user.logout();

            // this.appInsightsService.trackTrace(
            //   'Logging out previous session with different refresh token',
            //   {
            //     previousExpiration: this.apiToken?.expirationDate?.toISOString
            //       ? this.apiToken.expirationDate.toISOString()
            //       : null,
            //     previousUser: this.identity?.userName,
            //     newUser: identityToStore?.userName,
            //   },
            // );

            this.appInsightsService.trackTrace('Logging out previous session with different refresh token');

            return this.http
              .post(
                logOutUrl,
                { refreshToken: this.apiToken?.refreshToken },
                CombineOptions([RetryOption, WillHandleErrorMessagingOption]),
              )
              .pipe(
                tap({
                  complete: () => {
                    // Regardless of success or failure of logout call, complete the login
                    this.reset();
                    this.forceLogout = true;
                    this.completeLogin(response);
                  },
                }),
              );
          } else {
            this.completeLogin(response);
            return of(true);
          }
        }),
        catchError((err: HttpErrorResponse) => {
          //this.appInsightsService.trackException(err);

          this.appInsightsService.trackTrace(err.message);

          console.error(err);
          if ((loginOptions as RefreshLoginOptions).refreshToken) {
            this.router.navigate([AppUrls.logout()], {
              queryParams: { idle: true, state: 'showIdleLogoutWarning' },
            });
          } else {
            if (err.status === 0) {
              this.snackbarService.openSnackBarFailure('Connection failed.');
            } else {
              this.snackbarService.openSnackBarFailure('An error occurred.');
            }
            this.router.navigate([AppUrls.logout()]);
          }
          return EMPTY;
        }),
        shareReplay(1),
      );
    this.loginSubscription = this.login$.subscribe();
    return this.login$;
  }

  private completeLogin = (response: any) => {
    let identityToStore: IdentityUser = response.user;
    // if (!response.valid || !identityToStore) {
    //   this.appInsightsService.trackTrace('Invalid login.', null, SeverityLevel.Warning);
    // }

    if (!response.valid || !identityToStore) {
      this.appInsightsService.trackTrace('Invalid login.');
    }

    //Prevously, response.valid alone was used to determine login validity.
    //Now we are checking for a returned identity that matches the site (internal/external)
    //attempting to be logged in from the front end. This check ensures that the valid flag is in
    //sync with the identity, and logs errors on descrpancies (should not occur in prod, possible when testing).
    if (identityToStore) {
      let validUserError: Error;
      if (!response.valid) {
        validUserError = {
          name: 'UnexpectedResponse',
          message: 'User information should not have been returned for an invalid login.',
        };
      } else if (identityToStore.isExternalUser !== this.environmentService.isExternalSite) {
        validUserError = {
          name: 'UnexpectedResponse',
          message:
            'Login should have been invalid and user information should not have been returned for an invalid login.',
        };
      }
      if (validUserError) {
        identityToStore = null;
        //this.appInsightsService.trackException(validUserError);
        this.appInsightsService.trackTrace(validUserError.message);
      }
    }
    this.forceLogout = false;
    if (identityToStore) {
      response.token.lastLoginDateTime =
        identityToStore.userName === this.identity?.userName
          ? this.apiToken?.lastLoginDateTime
          : response.lastLoginDateTime;
    }
    this.updateToken(response.token);
    this.identity = identityToStore;
    if (this.isLoggedIn()) {
      this.onLogin.next();
      this.identityReloaded.next(this.identity);
    }
  };

  isLoggedIn = () =>
    this.apiToken?.accessToken != null &&
    this.identity != null &&
    this.isValidLogin !== false &&
    (this.isTokenValid() ||
      (this.apiToken.refreshToken &&
        this.apiToken.expirationDate &&
        moment()
          .subtract(this.environmentService.getEnvironment().refreshValidAfterTokenMins, 'minutes')
          .isBefore(this.apiToken.expirationDate)));

  isTokenValid = () =>
    this.apiToken?.expirationDate != null && moment().isBefore(this.apiToken.expirationDate)

  logout(showIdleLogoutWarning: boolean) {
    return this.redirectToLogout(showIdleLogoutWarning);
  }

  reset() {
    this.apiToken = this.identity = this.redirectPath = this.userGroups = null;
    sessionStorage.removeItem('bis_ssnSearch');
    this.onLogout.next();
  }

  redirectToThirdPartyLogin() {
    // Redirect user to login
    const params = new URLSearchParams();
    params.set('response_type', 'code');
    params.set('client_id', this._config.oAuthClientId);
    params.set('scope', 'openid offline_access profile');
    params.set('redirect_uri', location.origin + '/auth-callback');
    params.set('state', this.randomService.generateNonce(5));
    params.set('nonce', this.randomService.generateNonce(8));
    window.location.href = this._config.oAuthBaseAddress + `authorize?${params.toString()}`;

    return EMPTY;
  }

  getUserGroups = (entityTypeCodeFilters?: EntityTypeCode[]): Observable<Group[]> => {
    if (!this.userGroups) {
      const paginationRequest = {
        pageSize: 1000,
        pageOffset: 0,
        sorts: [],
        filters: [],
      };
      this.userGroups = this.http
        .post<PaginatedResponse>(this.userUrls.myGroups(), paginationRequest)
        .pipe(
          map((response: PaginatedResponse) => response.data),
          shareReplay(1),
        );
    }
    return (entityTypeCodeFilters?.length ?? 0) === 0
      ? this.userGroups
      : this.userGroups.pipe(
          /* eslint-disable indent*/
          map(groups =>
            _.filter(groups, group => entityTypeCodeFilters?.includes(group.entityTypeCode)),
          ),
        );
    /* eslint-enable indent*/
  };

  isUserMemberByEntityType(entityTypeCode: EntityTypeCode) {
    return (
      this.environmentService.isExternalSite &&
      this.identity.entityGroups.some(group => group.entityTypeCode === entityTypeCode)
    );
  }

  // -- Private helper methods

  private determineHasPermission(permissionName: string): boolean {
    return this.identity.permissions?.some(permission => permission.name === permissionName);
  }

  private determineHasRole(roleName: string): boolean {
    return this.identity.entityGroups?.some(entityGroup =>
      entityGroup.userRoleCodes?.some(role => role === roleName),
    );
  }

  private redirectToLogout(showIdleLogoutWarning: boolean): boolean {
    if (!this.apiToken?.idToken) {
      // We don't have the user's idToken, so we can't log them out of the third party auth
      return false;
    } else {
      // Redirect user to 3rd party logout
      const params = new URLSearchParams();
      params.set('id_token_hint', this.apiToken.idToken);
      params.set('client_id', this._config.oAuthClientId);
      params.set('post_logout_redirect_uri', location.origin + '/logout');
      if (showIdleLogoutWarning) {
        params.set('state', 'showIdleLogoutWarning');
      }
      window.sessionStorage.clear();
      window.location.href = this._config.oAuthBaseAddress + `logout?${params.toString()}`;
      return true;
    }
  }

  private updateToken(tokenResponse: any) {
    this.apiToken = {
      accessToken: tokenResponse.accessToken,
      refreshToken: tokenResponse.refreshToken,
      idToken: tokenResponse.idToken,
      expirationDate: moment().add(tokenResponse.expiresIn, 's'),
      lastLoginDateTime: moment(tokenResponse.lastLoginDateTime),
      apiSecurityHash: tokenResponse.apiSecurityHash,
    };
  }
}
